import React from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";

// Precise geographic coordinates (real lat/lng)
const CITIES = [
  { name: "Palm Beach",      lat: 26.7153, lng: -80.0534 },
  { name: "Fort Lauderdale", lat: 26.1224, lng: -80.1373 },
  { name: "Miami",           lat: 25.7617, lng: -80.1918 },
];

// Custom brand-styled pin (cyan glow with drop shadow)
const makeIcon = () =>
  L.divIcon({
    className: "mne-pin",
    html: `
      <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
        <span style="position:absolute;bottom:0;width:26px;height:26px;border-radius:50%;background:rgba(0,150,199,0.35);animation:mnePing 2s ease-out infinite;"></span>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#0B2440" stroke="#0096C7" stroke-width="2"
             style="filter: drop-shadow(0 0 10px rgba(0,150,199,0.9));">
          <path d="M12 22s-8-7.58-8-13a8 8 0 1 1 16 0c0 5.42-8 13-8 13z"/>
          <circle cx="12" cy="9" r="2.5" fill="#0096C7" stroke="none"/>
        </svg>
      </div>`,
    iconSize: [28, 34],
    iconAnchor: [14, 34],
    popupAnchor: [0, -30],
  });

const ROUTE = CITIES.map((c) => [c.lat, c.lng]);

// A tiny inline component to draw the coverage polyline via Leaflet
function CoverageLine() {
  const map = useMap();
  React.useEffect(() => {
    const line = L.polyline(ROUTE, {
      color: "#C8862E",
      weight: 2,
      dashArray: "6 6",
      opacity: 0.9,
    }).addTo(map);
    return () => { line.remove(); };
  }, [map]);
  return null;
}

export default function ServiceMap() {
  const { t } = useApp();

  return (
    <section id="area" className="relative py-28 lg:py-40 bg-ink text-white overflow-hidden" data-testid="area-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[440px_1fr] gap-14 items-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.03]">{t.area.title}</h2>
          <p className="mt-6 text-slate leading-relaxed">{t.area.sub}</p>
          <p className="mt-4 text-brass text-sm leading-relaxed border-l-2 border-brass/50 pl-4">{t.area.allUS}</p>
          <ul className="mt-8 grid grid-cols-2 gap-3">
            {t.area.marinas.map((m) => (
              <li key={m} className="font-mono text-xs text-slate flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-brass" />{m}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            className="relative aspect-[5/6] max-w-md mx-auto rounded-2xl overflow-hidden ring-1 ring-cyan/25 bg-navy shadow-[0_30px_80px_-20px_rgba(0,150,199,0.35)]"
            data-testid="service-map"
          >
            <MapContainer
              center={[26.3, -80.13]}
              zoom={9}
              minZoom={8}
              maxZoom={12}
              scrollWheelZoom={false}
              zoomControl={false}
              attributionControl={false}
              style={{ width: "100%", height: "100%" }}
            >
              {/* ESRI World Imagery — free, key-less satellite basemap */}
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Tiles &copy; Esri"
              />
              {/* Labels (place names) overlay */}
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                opacity={0.9}
              />
              <CoverageLine />
              {CITIES.map((c) => (
                <Marker key={c.name} position={[c.lat, c.lng]} icon={makeIcon()}>
                  <Tooltip direction="top" offset={[0, -28]} opacity={1} permanent
                    className="mne-tooltip">
                    {c.name}
                  </Tooltip>
                  <Popup>
                    <strong>{c.name}</strong><br/>
                    {c.lat.toFixed(4)}° N · {Math.abs(c.lng).toFixed(4)}° W
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* subtle dark grade overlay so labels/pins pop */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-ink/25 via-transparent to-ink/50" />

            {/* HUD chips */}
            <div className="absolute top-3 left-3 z-[400] font-mono text-[10px] tracking-wider text-cyan/95 bg-ink/70 backdrop-blur-sm border border-cyan/30 px-2 py-1 rounded">
              26.12° N · 80.14° W
            </div>
            <div className="absolute bottom-3 right-3 z-[400] font-mono text-[10px] tracking-wider text-white/85 bg-ink/70 backdrop-blur-sm border border-white/15 px-2 py-1 rounded">
              GOLD COAST
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
